import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
	User,
	KeyRound,
	Mail,
	Copy,
	Gift,
	AlertCircle,
	Loader2,
	Check,
} from "lucide-react";
import { getUserDetails, updateUserPassword } from "../actions/userActions";
import { FormInput } from "../components/FormInput";

const ErrorMessage = ({ message }) => (
	<div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4 flex items-start gap-3">
		<AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
		<p>{message}</p>
	</div>
);

const SuccessMessage = ({ message }) => (
	<div className="bg-green-50 border border-green-200 text-green-600 rounded-lg p-4 flex items-start gap-3">
		<Check className="w-5 h-5 mt-0.5 flex-shrink-0" />
		<p>{message}</p>
	</div>
);

const ProfileScreen = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [formData, setFormData] = useState({
		password: "",
		confirmPassword: "",
	});

	const [formErrors, setFormErrors] = useState({
		password: "",
		confirmPassword: "",
	});

	const [refCode, setRefCode] = useState("");
	const [message, setMessage] = useState("");
	const [copied, setCopied] = useState(false);

	const userDetails = useSelector((state) => state.userDetails);
	const { loading, error, user } = userDetails;

	const userLogin = useSelector((state) => state.userLogin);
	const { userInfo } = userLogin;

	useEffect(() => {
		if (!userInfo) {
			navigate("/login");
		} else {
			dispatch(getUserDetails(userInfo, navigate));
		}
	}, [navigate, dispatch, userInfo]);

	useEffect(() => {
		if (user?.id) {
			setRefCode((user?.name?.split(" ")?.[0] + user?.id)?.toUpperCase());
		}
	}, [user]);

	const validatePassword = (password) => {
		if (password.length < 6 || !password.match("[0-9]")) {
			return "Password must be at least 6 characters and contain numbers";
		}
		return "";
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));

		// Password validation
		if (name === "password") {
			const error = validatePassword(value);
			setFormErrors((prev) => ({
				...prev,
				password: error,
				confirmPassword:
					value !== formData.confirmPassword ? "Passwords must match" : "",
			}));
		}

		// Confirm password validation
		if (name === "confirmPassword") {
			setFormErrors((prev) => ({
				...prev,
				confirmPassword:
					value !== formData.password ? "Passwords must match" : "",
			}));
		}
	};

	const handleCopyRefCode = () => {
		navigator.clipboard.writeText(refCode);
		setCopied(true);
		setMessage("Referral code copied to clipboard!");
		setTimeout(() => {
			setCopied(false);
			setMessage("");
		}, 3000);
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		if (formData.password !== formData.confirmPassword) {
			setFormErrors((prev) => ({
				...prev,
				confirmPassword: "Passwords do not match",
			}));
			return;
		}

		if (!formErrors.password && !formErrors.confirmPassword) {
			dispatch(updateUserPassword(userInfo, formData.password, navigate));
		}
	};

	return (
		<div className="min-h-screen bg-gray-50 py-8">
			<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="flex items-center gap-3 mb-8">
					<User className="w-8 h-8 text-primary" />
					<h1 className="text-2xl font-bold">My Profile</h1>
				</div>

				{/* Referral Section */}
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
					<div className="flex items-start gap-4">
						<div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
							<Gift className="w-6 h-6 text-primary" />
						</div>
						<div className="flex-1">
							<h2 className="text-lg font-semibold mb-2">
								Share & Save with Our Referral Program!
							</h2>
							<p className="text-gray-600 mb-6">
								Invite friends and unlock rewards! Refer someone, and you'll
								earn credits for your next purchase, while your friend gets 10%
								off their order. It's a win-win! Enjoy the benefits together!
							</p>

							<div className="flex items-center gap-3">
								<div className="flex-1">
									<input
										type="text"
										value={refCode}
										readOnly
										className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 font-medium"
									/>
								</div>
								<button
									onClick={handleCopyRefCode}
									className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
										copied
											? "bg-green-100 text-green-600"
											: "bg-primary text-white hover:bg-primary/90"
									}`}
								>
									{copied ? (
										<>
											<Check className="w-5 h-5" />
											Copied!
										</>
									) : (
										<>
											<Copy className="w-5 h-5" />
											Copy Code
										</>
									)}
								</button>
							</div>
						</div>
					</div>
				</div>

				{/* Profile Section */}
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
					<h2 className="text-lg font-semibold mb-6">Account Information</h2>

					{error && <ErrorMessage message={error} />}
					{message && <SuccessMessage message={message} />}

					<form onSubmit={handleSubmit} className="space-y-6 max-w-md">
						<FormInput
							label="Name"
							type="text"
							value={user?.name || ""}
							icon={User}
							disabled
						/>

						<FormInput
							label="Email"
							type="email"
							value={user?.email || ""}
							icon={Mail}
							disabled
						/>

						<div className="pt-4 border-t border-gray-200">
							<h3 className="text-md font-medium mb-4">Change Password</h3>

							<div className="space-y-4">
								<FormInput
									label="New Password"
									name="password"
									type="password"
									icon={KeyRound}
									value={formData.password}
									onChange={handleInputChange}
									error={formErrors.password}
									placeholder="Enter new password"
								/>

								<FormInput
									label="Confirm New Password"
									name="confirmPassword"
									type="password"
									icon={KeyRound}
									value={formData.confirmPassword}
									onChange={handleInputChange}
									error={formErrors.confirmPassword}
									placeholder="Confirm new password"
								/>
							</div>
						</div>

						<div className="flex items-center justify-between pt-4">
							<button
								type="submit"
								disabled={
									loading || Object.values(formErrors).some((error) => error)
								}
								className="px-6 py-2 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
							>
								{loading ? (
									<>
										<Loader2 className="w-5 h-5 animate-spin" />
										Updating...
									</>
								) : (
									"Update Password"
								)}
							</button>
						</div>
					</form>
				</div>
			</div>

			{/* Loading Overlay */}
			<AnimatePresence>
				{loading && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className="fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50"
					>
						<div className="flex flex-col items-center gap-4">
							<div className="relative w-32 h-32">
								<img
									src="/images/logoDark.png"
									alt="Logo"
									className="w-full h-full object-contain"
								/>
								<div className="absolute inset-0 border-t-4 border-primary rounded-full animate-spin" />
							</div>
							<p className="text-lg font-medium text-gray-900">
								Updating your profile...
							</p>
						</div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};

export default ProfileScreen;
